import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContactStaggeredMenu from './ContactStaggeredMenu';
import SignInStaggeredMenu from './SignInStaggeredMenu';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactMenuOpen, setContactMenuOpen] = useState(false);
  const [signInMenuOpen, setSignInMenuOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [scrolled, setScrolled] = useState(false);
  const [currentSection, setCurrentSection] = useState('hero');
  const location = useLocation();
  const navigate = useNavigate();
  const { lang = 'en' } = useParams();
  const { t } = useTranslation('common');

  // Helper to get language-prefixed path
  const getLocalizedPath = (path) => `/${lang}${path}`;

  useEffect(() => {
    const handleScroll = () => {
      // Detect which section we're in using data-section attribute
      const sections = ['hero', 'services', 'cases', 'technologies', 'why-choose-us-home', 'process', 'contact', 'about-intro', 'why-choose-us', 'team', 'timeline', 'cta'];
      const sectionElements = sections.map(name =>
        document.querySelector(`[data-section="${name}"]`)
      );

      let current = 'hero';
      let found = false;
      sectionElements.forEach((element, index) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            current = sections[index];
            found = true;
          }
        }
      });

      // If no section matches (e.g., in a margin gap) and we are scrolled down,
      // default to a safe "white" section style (treated as non-dark).
      if (!found && window.scrollY > 100) {
        current = 'gap';
      }

      setCurrentSection(current);

      // Only set scrolled to true if we're past the hero section
      const heroElement = document.querySelector('[data-section="hero"]');
      if (heroElement) {
        const heroRect = heroElement.getBoundingClientRect();
        setScrolled(heroRect.bottom <= 0);
      }
    };

    // Simple throttle implementation to avoid excessive layout calculations
    let lastScroll = 0;
    const throttleDelay = 100; // ms

    const onScroll = () => {
      const now = Date.now();
      if (now - lastScroll >= throttleDelay) {
        handleScroll();
        lastScroll = now;
      }
    };

    window.addEventListener('scroll', onScroll);
    // Use requestAnimationFrame for the initial check to ensure DOM is ready
    requestAnimationFrame(handleScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]); // Re-run when route changes

  // Determine navbar style based on section
  const getNavbarStyle = () => {
    // Normalize path to remove language prefix for comparison
    const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, '/').replace(/^\/[a-z]{2}$/, '/');

    // If we're on the main services page (/services), use dark navbar (matches dark bento grid)
    // But switch to white navbar when on the contact section
    // Service detail pages (/services/web-development) use white navbar
    if (pathWithoutLang === '/services') {
      // Check if we're on the contact section
      if (currentSection === 'contact') {
        return {
          bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
          text: 'text-black',
          logo: '/logo-black.png',
          border: 'border-black/20',
          buttonBorder: 'border-black',
          buttonBg: 'bg-black/5',
          buttonHoverBg: 'hover:bg-black',
          buttonHoverText: 'hover:text-white'
        };
      }

      return {
        bg: 'bg-black',
        text: 'text-white',
        logo: '/logo-white.png',
        border: 'border-white/20',
        buttonBorder: 'border-white',
        buttonBg: 'bg-white/10',
        buttonHoverBg: 'hover:bg-white',
        buttonHoverText: 'hover:text-black'
      };
    }

    // Service detail pages use white navbar
    if (pathWithoutLang.startsWith('/services/')) {
      return {
        bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
        text: 'text-black',
        logo: '/logo-black.png',
        border: 'border-gray-200',
        buttonBorder: 'border-black',
        buttonBg: 'bg-black/5',
        buttonHoverBg: 'hover:bg-black',
        buttonHoverText: 'hover:text-white'
      };
    }

    // If we're on the blogs pages, use white navbar with black elements
    if (pathWithoutLang.startsWith('/blogs')) {
      return {
        bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
        text: 'text-black',
        logo: '/logo-black.png',
        border: 'border-gray-200',
        buttonBorder: 'border-black',
        buttonBg: 'bg-black/5',
        buttonHoverBg: 'hover:bg-black',
        buttonHoverText: 'hover:text-white'
      };
    }

    // If we're on the get-quote page, use white navbar with black elements
    if (pathWithoutLang.startsWith('/get-quote')) {
      return {
        bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
        text: 'text-black',
        logo: '/logo-black.png',
        border: 'border-gray-200',
        buttonBorder: 'border-black',
        buttonBg: 'bg-black/5',
        buttonHoverBg: 'hover:bg-black',
        buttonHoverText: 'hover:text-white'
      };
    }

    // If we're on the book page, use white navbar with black elements
    if (pathWithoutLang.startsWith('/book')) {
      return {
        bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
        text: 'text-black',
        logo: '/logo-black.png',
        border: 'border-gray-200',
        buttonBorder: 'border-black',
        buttonBg: 'bg-black/5',
        buttonHoverBg: 'hover:bg-black',
        buttonHoverText: 'hover:text-white'
      };
    }

    // If we're on the cases pages, use dark navbar unless on contact section
    if (pathWithoutLang.startsWith('/cases')) {
      // Check if we're on the contact section
      if (currentSection === 'contact') {
        return {
          bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
          text: 'text-black',
          logo: '/logo-black.png',
          border: 'border-black/20',
          buttonBorder: 'border-black',
          buttonBg: 'bg-black/5',
          buttonHoverBg: 'hover:bg-black',
          buttonHoverText: 'hover:text-white'
        };
      }

      return {
        bg: 'bg-black',
        text: 'text-white',
        logo: '/logo-white.png',
        border: 'border-white/20',
        buttonBorder: 'border-white',
        buttonBg: 'bg-white/10',
        buttonHoverBg: 'hover:bg-white',
        buttonHoverText: 'hover:text-black'
      };
    }

    // Dark sections: hero, cases, why-choose-us, timeline
    const darkSections = ['hero', 'cases', 'why-choose-us', 'why-choose-us-home', 'timeline'];

    if (darkSections.includes(currentSection)) {
      return {
        bg: currentSection === 'hero' ? 'bg-transparent' : 'bg-black',
        // Mobile menu needs solid background, not transparent
        mobileBg: 'bg-black',
        text: 'text-white',
        logo: '/logo-white.png',
        border: 'border-white/20',
        buttonBorder: 'border-white',
        buttonBg: 'bg-white/10',
        buttonHoverBg: 'hover:bg-white',
        buttonHoverText: 'hover:text-black'
      };
    } else {
      // White sections: services, technologies, process, contact, about-intro, team, cta
      return {
        bg: 'bg-white/95 backdrop-blur-sm shadow-sm',
        mobileBg: 'bg-white',
        text: 'text-black',
        logo: '/logo-black.png',
        border: 'border-gray-200',
        buttonBorder: 'border-black',
        buttonBg: 'bg-black/5',
        buttonHoverBg: 'hover:bg-black',
        buttonHoverText: 'hover:text-white'
      };
    }
  };

  const style = getNavbarStyle();

  // When mobile menu is open on a transparent background, use solid background
  const navBg = mobileMenuOpen && style.bg === 'bg-transparent'
    ? (style.mobileBg || 'bg-black')
    : style.bg;

  return (
    <nav className={`${navBg} sticky top-0 z-50 transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <a
                href={getLocalizedPath('')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getLocalizedPath(''));
                }}
                className="focus:outline-none"
                aria-label="Go to home page"
              >
                <img
                  src={style.logo}
                  alt="Apptek Logo"
                  fetchPriority="high"
                  loading="eager"
                  className="h-5 w-auto transition-all duration-300"
                />
              </a>
            </motion.div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Navigation links moved to CTA section */}
          </div>

          {/* Desktop CTA Buttons */}
          <motion.div
            className="hidden md:flex items-center"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <a
              href={getLocalizedPath('/services')}
              onClick={(e) => {
                e.preventDefault();
                navigate(getLocalizedPath('/services'));
              }}
              className={`${style.text} hover:opacity-80 transition-colors duration-200 px-4 py-2 mr-1`}
            >
              {t('nav.services')}
            </a>
            <a
              href={getLocalizedPath('/cases')}
              onClick={(e) => {
                e.preventDefault();
                navigate(getLocalizedPath('/cases'));
              }}
              className={`${style.text} hover:opacity-80 transition-colors duration-200 px-4 py-2 mr-2`}
            >
              {t('nav.cases')}
            </a>
            <button
              onClick={() => navigate(getLocalizedPath('/about-us'))}
              className={`${style.text} hover:opacity-80 transition-colors duration-200 px-4 py-2 mr-2`}
            >
              {t('nav.aboutUs')}
            </button>
            <button
              onClick={() => setSignInMenuOpen(true)}
              className={`px-5 py-2 border ${style.buttonBorder} ${style.text} rounded-full ${style.buttonHoverBg} ${style.buttonHoverText} transition-all duration-200 text-sm mr-2`}
            >
              {t('nav.signIn')}
            </button>
            <button
              onClick={() => setContactMenuOpen(true)}
              className="px-5 py-2 bg-[#073A29] text-white rounded-full hover:bg-opacity-90 hover:shadow-lg transition-all duration-200 text-sm mr-3"
            >
              {t('nav.contactUs')}
            </button>
            {/* Language Switcher - Globe icon dropdown */}
            <div className={`border-l ${style.buttonBorder} pl-3`}>
              <LanguageSwitcher className={style.text} />
            </div>
          </motion.div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`${style.text} hover:opacity-70 transition-opacity`}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {
        mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`absolute top-full left-0 w-full md:hidden ${style.mobileBg || style.bg} border-t ${style.border} shadow-xl`}
          >
            <div className="px-4 pt-2 pb-4 space-y-2">
              <a
                href={getLocalizedPath('/services')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getLocalizedPath('/services'));
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 ${style.text} hover:opacity-80`}
              >
                {t('nav.services')}
              </a>
              <a
                href={getLocalizedPath('/cases')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getLocalizedPath('/cases'));
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 ${style.text} hover:opacity-80`}
              >
                {t('nav.cases')}
              </a>
              <button
                onClick={() => {
                  navigate(getLocalizedPath('/about-us'));
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 ${style.text} hover:opacity-80 w-full text-left`}
              >
                {t('nav.aboutUs')}
              </button>
              <div className="pt-2">
                <LanguageSwitcher className={style.text} />
              </div>
              <div className="pt-4 space-y-2">
                <button
                  onClick={() => {
                    setSignInMenuOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full px-5 py-2 border ${style.buttonBorder} ${style.text} rounded-full text-sm`}
                >
                  {t('nav.signIn')}
                </button>
                <button
                  onClick={() => {
                    setContactMenuOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-5 py-2 bg-[#073A29] text-white rounded-full text-sm"
                >
                  {t('nav.contactUs')}
                </button>
              </div>
            </div>
          </motion.div>
        )
      }

      {/* Contact Staggered Menu */}
      {
        contactMenuOpen && (
          <ContactStaggeredMenu
            position="right"
            items={[
              { label: 'Get Started', ariaLabel: 'Start your project', link: '#contact' },
              { label: 'Our Work', ariaLabel: 'View case studies', link: '#cases' },
              { label: 'Services', ariaLabel: 'View our services', link: '#services' },
            ]}
            socialItems={[
              { label: 'Twitter', link: 'https://twitter.com/apptek' },
              { label: 'LinkedIn', link: 'https://linkedin.com/company/apptek' },
              { label: 'GitHub', link: 'https://github.com/apptek' },
              { label: 'Email Us', link: 'mailto:hello@apptek.com' }
            ]}
            displaySocials={true}
            displayItemNumbering={true}
            accentColor="#073A29"
            onMenuOpen={() => console.log('Contact menu opened')}
            onMenuClose={() => {
              console.log('Contact menu closed');
              setContactMenuOpen(false);
            }}
          />
        )
      }

      {/* Sign In Staggered Menu */}
      {
        signInMenuOpen && (
          <SignInStaggeredMenu
            position="right"
            colors={['#0c6749ff', '#073A29']}
            accentColor="#073A29"
            onMenuOpen={() => console.log('Sign in menu opened')}
            onMenuClose={() => {
              console.log('Sign in menu closed');
              setSignInMenuOpen(false);
            }}
            onContactUs={() => {
              setSignInMenuOpen(false); // Close sign-in menu
              setContactMenuOpen(true); // Open contact menu
            }}
          />
        )
      }
    </nav >
  );
};

export default Navbar;
